import React, { useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { ORG_LABELS, TEAM_LABELS } from '../config';
import { fmtDate, useFetch, usePaged } from '../hooks';
import { Alert, Badge, Button, Card, Field, Input, Select, SearchBox } from '../ui/basics';
import { DataTable } from '../ui/DataTable';
import { Confirm, Drawer, useToast } from '../ui/overlay';
import UserForm from './users/UserForm';

function ResetPassword({ user, onClose }) {
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [temp, setTemp] = useState('');

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await api.post(`users/${user.id}/reset-password/`, password ? { password } : {});
      if (res.temporary_password) setTemp(res.temporary_password);
      else { toast('Password reset', 'success'); onClose(); }
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  return (
    <Drawer center narrow title={`Reset password · ${user.email}`} onClose={onClose}
      footer={temp ? <Button variant="primary" onClick={onClose}>Done</Button> : <><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} onClick={submit}>Reset password</Button></>}>
      {temp ? (
        <div className="stack"><Alert tone="info">Share this temporary password securely. It is shown only once.</Alert>
          <Input readOnly value={temp} onFocus={(e) => e.target.select()} style={{ fontFamily: 'monospace' }} /></div>
      ) : (
        <div className="stack">{error && <Alert>{error}</Alert>}
          <p className="muted">The user will be signed out everywhere and must choose a new password at next sign-in.</p>
          <Field label="New temporary password" hint="Leave empty to generate one."><Input autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} /></Field></div>
      )}
    </Drawer>
  );
}

export default function Users() {
  const { user: me } = useAuth();
  const toast = useToast();
  const options = useFetch('access/options/');
  const paged = usePaged('users/', { initialOrdering: 'email' });
  const { filters, setFilter, clearFilters, reload } = paged;
  const [dialog, setDialog] = useState(null); // { type: 'form'|'reset'|'delete', user? }
  const [busy, setBusy] = useState(false);
  const done = () => { setDialog(null); reload(); };

  const remove = async () => {
    setBusy(true);
    try { await api.delete(`users/${dialog.user.id}/`); toast('User deleted', 'success'); done(); }
    catch (e) { toast(e.message, 'error'); setDialog(null); }
    setBusy(false);
  };
  const toggleActive = async () => {
    const u = dialog.user;
    setBusy(true);
    try { await api.patch(`users/${u.id}/`, { is_active: !u.is_active }); toast(u.is_active ? 'User deactivated' : 'User activated', 'success'); setDialog(null); reload(); }
    catch (e) { toast(e.message, 'error'); setDialog(null); }
    setBusy(false);
  };

  const chips = (list, labels) => (list.length ? list.map((k) => <Badge key={k}>{labels[k] || k}</Badge>) : <span className="muted">—</span>);
  const columns = [
    { key: 'email', label: 'User', sortable: true, render: (u) => (
      <div><b>{u.display_name || u.email}</b>{u.id === me.id && <> <Badge tone="accent">You</Badge></>}<div className="muted">{u.email}</div></div>) },
    { key: 'role', label: 'Role', sortable: true, render: (u) => u.role || <span className="muted">Custom</span> },
    { key: 'organizations', label: 'Organizations', render: (u) => <div className="row" style={{ gap: 4 }}>{chips(u.organizations, ORG_LABELS)}</div> },
    { key: 'pages', label: 'Pages', render: (u) => <div className="row" style={{ gap: 4 }}>{chips(u.pages, {})}</div> },
    { key: 'teams', label: 'Teams', render: (u) => (u.teams.includes('*') ? <Badge tone="accent">All teams</Badge> : <div className="row" style={{ gap: 4 }}>{chips(u.teams, TEAM_LABELS)}</div>) },
    { key: 'is_active', label: 'Status', sortable: true, render: (u) => (
      <div className="row" style={{ gap: 6 }}><Badge tone={u.is_active ? 'success' : 'danger'}>{u.is_active ? 'Active' : 'Disabled'}</Badge>
        {u.must_change_password && <Badge tone="warning">Password pending</Badge>}{!u.can_edit && <Badge>View only</Badge>}</div>) },
    { key: 'last_login', label: 'Last sign-in', sortable: true, render: (u) => (u.last_login ? fmtDate(u.last_login) : <span className="muted">Never</span>) },
    { key: 'actions', label: '', align: 'right', render: (u) => (
      <div className="row" style={{ justifyContent: 'flex-end', gap: 2, flexWrap: 'nowrap' }}>
        <Button variant="ghost" size="sm" icon="edit" aria-label="Edit user" onClick={() => setDialog({ type: 'form', user: u })} />
        <Button variant="ghost" size="sm" icon="key" aria-label="Reset password" onClick={() => setDialog({ type: 'reset', user: u })} />
        {u.id !== me.id && <Button variant="ghost" size="sm" onClick={() => setDialog({ type: 'toggle', user: u })}>{u.is_active ? 'Disable' : 'Enable'}</Button>}
        {u.id !== me.id && <Button variant="ghost" size="sm" icon="trash" aria-label="Delete user" onClick={() => setDialog({ type: 'delete', user: u })} />}
      </div>) },
  ];

  return (
    <div className="stack">
      <div className="page-head">
        <div><h1>Users & Access</h1><p>Control who can sign in, and which organizations, pages and teams they can see.</p></div>
        <div className="actions">
          {me.can_edit
            ? <Button variant="primary" icon="plus" disabled={!options.data} onClick={() => setDialog({ type: 'form' })}>Add user</Button>
            : <Badge>View only</Badge>}
        </div>
      </div>
      <Card flush>
        <div className="toolbar">
          <SearchBox value={filters.search} onChange={(v) => setFilter('search', v)} placeholder="Search by name or email…" />
          <Select value={filters.role || ''} onChange={(e) => setFilter('role', e.target.value)} options={options.data ? options.data.data.roles : []} placeholder="All roles" aria-label="Role" />
          <Select value={filters.is_active || ''} onChange={(e) => setFilter('is_active', e.target.value)} options={[{ value: 'true', label: 'Active' }, { value: 'false', label: 'Disabled' }]} placeholder="Any status" aria-label="Status" />
          {Object.values(filters).some(Boolean) && <Button variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>}
        </div>
        <DataTable columns={me.can_edit ? columns : columns.filter((c) => c.key !== 'actions')} paged={paged} rowKey={(u) => u.id} noun="users" />
      </Card>
      {dialog && dialog.type === 'form' && options.data && <UserForm user={dialog.user} options={options.data.data} onClose={() => setDialog(null)} onSaved={done} />}
      {dialog && dialog.type === 'reset' && <ResetPassword user={dialog.user} onClose={done} />}
      {dialog && dialog.type === 'toggle' && (
        <Confirm danger={dialog.user.is_active} busy={busy} onClose={() => setDialog(null)} onConfirm={toggleActive}
          title={dialog.user.is_active ? 'Disable user' : 'Enable user'} confirmLabel={dialog.user.is_active ? 'Disable' : 'Enable'}
          message={dialog.user.is_active
            ? `Disable ${dialog.user.email}? They will be signed out and cannot sign in until you enable the account again. Their data is kept.`
            : `Enable ${dialog.user.email}? They will be able to sign in again with their existing password.`} />
      )}
      {dialog && dialog.type === 'delete' && <Confirm danger busy={busy} title="Delete user" confirmLabel="Delete" onClose={() => setDialog(null)} onConfirm={remove}
        message={`Permanently delete ${dialog.user.email}? They will lose access immediately. To keep their record, disable the account instead.`} />}
    </div>
  );
}
